// Mailboxes — the sending fleet: identity, caps, sends today, live health.
import React, { useState, useEffect, useCallback } from 'react';
import { HeartPulse, RefreshCw, Play, Pause, Zap } from 'lucide-react';
import { apiService } from '../services/apiService';
import { Card, Chip, Btn, Spinner, useToasts, fmtDateTime } from './ui';

const HEALTH_TONE = { healthy: 'green', error: 'red', paused: 'gray' };

const Mailboxes = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const [push, toasts] = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getMailboxSummary();
      setAccounts(res.accounts || []);
    } catch (e) {
      push(`Failed to load mailboxes: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => { load(); }, [load]);

  const runTest = async (a) => {
    setTesting((t) => ({ ...t, [a.id]: true }));
    try {
      const res = await apiService.testMailbox(a.id);
      if (res.status === 'success') push(`${a.email}: IMAP + SMTP both working`);
      else push(`${a.email}: IMAP ${res.imap} · SMTP ${res.smtp}`, 'error');
      load();
    } catch (e) {
      push(`Test failed: ${e.message}`, 'error');
    } finally {
      setTesting((t) => ({ ...t, [a.id]: false }));
    }
  };

  const toggleActive = async (a) => {
    try {
      await apiService.updateMailbox(a.id, { is_active: !a.is_active });
      push(`${a.email} ${a.is_active ? 'paused' : 'resumed'}`);
      load();
    } catch (e) {
      push(`Update failed: ${e.message}`, 'error');
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--c-text)' }}>Mailboxes</div>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>
            The sending fleet — checked hourly; caps apply per batch (2 batches per day, Mon–Fri).
          </div>
        </div>
        <Btn small onClick={load}><RefreshCw size={13} /> Refresh</Btn>
      </div>

      {loading && <div style={{ padding: 60, textAlign: 'center' }}><Spinner size={24} /></div>}

      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {accounts.map((a) => {
            const pct = a.daily_ceiling ? Math.min(100, Math.round((a.sent_today / a.daily_ceiling) * 100)) : 0;
            return (
              <Card key={a.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.display_name || a.email}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.email}{a.sig_title ? ` · ${a.sig_title}` : ''}
                    </div>
                  </div>
                  <Chip tone={HEALTH_TONE[a.health] || 'gray'}>
                    <HeartPulse size={11} /> {a.health}
                  </Chip>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Chip tone={a.provider === 'domain' ? 'purple' : 'blue'}>{a.provider}</Chip>
                  {a.use_for_outreach && <Chip tone="green">outreach rotation</Chip>}
                  {!a.is_active && <Chip tone="gray">paused</Chip>}
                </div>

                {/* Today's sends vs ceiling */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--c-text-3)', marginBottom: 4 }}>
                    <span>Sent today</span>
                    <span style={{ fontWeight: 700, color: 'var(--c-text-2)' }}>{a.sent_today} / {a.daily_ceiling}</span>
                  </div>
                  <div style={{ height: 7, borderRadius: 5, background: 'var(--c-surface)', overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: 5,
                      background: pct >= 100 ? 'var(--c-yellow)' : 'linear-gradient(90deg, var(--c-primary), var(--c-accent))',
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </div>

                {a.last_error && (
                  <div style={{ fontSize: 12, color: 'var(--c-red)', background: 'rgba(239,68,68,0.08)', borderRadius: 8, padding: '7px 10px' }}>
                    {a.last_error}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                  <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>
                    synced {a.last_synced_at ? fmtDateTime(a.last_synced_at) : 'never'}
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn small onClick={() => runTest(a)} disabled={!!testing[a.id]} title="Live IMAP + SMTP login test">
                      <Zap size={12} /> {testing[a.id] ? 'Testing…' : 'Test'}
                    </Btn>
                    <Btn small onClick={() => toggleActive(a)} title={a.is_active ? 'Pause this mailbox' : 'Resume this mailbox'}>
                      {a.is_active ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
                    </Btn>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {toasts}
    </div>
  );
};

export default Mailboxes;
